import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { PanelEdgeTab } from '@signals/app-shell/features/layout/PanelEdgeTab';
import { useFeatureFlagsStore } from '@signals/app-shell/stores/featureFlagsStore';
import { PacketMapPoint, PacketMapPolygon } from '@signals/app-shell/services/intelPacketApi';
import {
  Mission,
  MissionRouteStatus,
  getMission,
} from '@signals/app-shell/services/missionApi';
import RouteReferenceDock, { DockTabId } from '@signals/app-shell/features/routePlanner/dock/RouteReferenceDock';
import { useMissionScan } from '@signals/app-shell/features/routePlanner/dock/useMissionScan';
import MissionAreasTab from '@signals/app-shell/features/missions/MissionAreasTab';
import MissionCotravelers from '@signals/app-shell/features/missions/MissionCotravelers';
import { useMissionDockLive } from '@signals/app-shell/features/missions/useMissionDockLive';
import MissionCrewChat from '@signals/app-shell/features/missions/MissionCrewChat';
import MissionChatToast from '@signals/app-shell/features/missions/MissionChatToast';
import MissionVehicleBar from '@signals/app-shell/features/missions/MissionVehicleBar';
import MissionVideoStrip from '@signals/app-shell/features/missions/MissionVideoStrip';
import MissionPhotosTab from '@signals/app-shell/features/missions/MissionPhotosTab';
import MissionDwellsTab from '@signals/app-shell/features/missions/MissionDwellsTab';
import MissionSignalsTab from '@signals/app-shell/features/missions/MissionSignalsTab';
import MissionReportTab from '@signals/app-shell/features/missions/MissionReportTab';
import MissionDeviceLayer from '@signals/app-shell/features/missions/map/MissionDeviceLayer';
import MissionThreatLayer from '@signals/app-shell/features/missions/map/MissionThreatLayer';
import MissionMediaLayer from '@signals/app-shell/features/missions/map/MissionMediaLayer';
import MissionDwellLayer from '@signals/app-shell/features/missions/map/MissionDwellLayer';
import MissionOptionPreviewLayer, {
  RouteOptionPreview,
} from '@signals/app-shell/features/missions/map/MissionOptionPreviewLayer';
import MissionRouteLayer from '@signals/app-shell/features/missions/map/MissionRouteLayer';
import MissionWaypointLayer from '@signals/app-shell/features/missions/map/MissionWaypointLayer';
import { pickTrailLead, reportedProgress } from '@signals/app-shell/features/missions/map/missionRouteTrail';
import { useMissionDeviceTracks } from '@signals/app-shell/features/missions/useMissionDeviceTracks';
import { useConvoyRedirect } from '@signals/app-shell/features/missions/convoyRedirect';
import {
  AreaDrawOverlay,
  AreaDrawPreview,
  useAreaDraw,
} from '@signals/app-shell/features/missions/AreaDrawTool';
import { criticalAlertCount } from '@signals/app-shell/features/missions/alertWaterfall';
import { watchlistLabels } from '@signals/app-shell/features/missions/areaPresence';
import { useStopSentinelLive } from '@signals/app-shell/features/missions/useStopSentinelLive';
import IntelPacketPanel, { FileSelection } from '../components/pathfinder/IntelPacketPanel';
import IntelPacketMap, { IntelMapLayer } from '../components/pathfinder/intel/IntelPacketMap';
import CompactTimeSlider from '../components/pathfinder/CompactTimeSlider';
import { PathfinderDataPoint } from '../services/pathfinderApi';

const MISSION_LAYER = 'intel-mission';

const IntelPacketPage: React.FC = () => {
  const theme = useTheme();
  const { packetId, missionId } = useParams();
  const navigate = useNavigate();
  const intelEnabled = useFeatureFlagsStore((s) => s.intel);
  const selected: FileSelection | null = useMemo(() => {
    if (missionId && /^\d+$/.test(missionId)) return { kind: 'mission', id: Number(missionId) };
    if (packetId && /^\d+$/.test(packetId)) return { kind: 'packet', id: Number(packetId) };
    return null;
  }, [packetId, missionId]);

  const [center, setCenter] = useState<[number, number]>([27.9475, -82.4584]);
  const [zoom, setZoom] = useState(10);
  const [layerMap, setLayerMap] = useState<Record<string, PacketMapPoint[]>>({});
  const [lineMap, setLineMap] = useState<Record<string, GeoJSON.LineString>>({});
  const [geofences, setGeofences] = useState<PacketMapPolygon[]>([]);
  // Held here rather than inside the panel because the reference dock sits
  // outside it, on the other side of the map.
  const [mission, setMission] = useState<Mission | null>(null);
  /** Tells the panel its copy of the open mission is behind ours. */
  const [missionSignal, setMissionSignal] = useState(0);
  /** Which mission the run status belongs to, so a redraw is not a reselect. */
  const shownIdRef = useRef<number | null>(null);
  const [runStatus, setRunStatus] = useState<MissionRouteStatus | null>(null);
  const [dockTabRequest, setDockTabRequest] = useState<{ id: DockTabId; key: number } | null>(null);
  const [optionPreview, setOptionPreview] = useState<RouteOptionPreview | null>(null);
  const [mapSettingsOpen, setMapSettingsOpen] = useState(false);
  const [timeWindow, setTimeWindow] = useState<{ start: Date; end: Date } | null>(null);
  const [fullExtent, setFullExtent] = useState(true);
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = Number(window.localStorage.getItem('intel-panel-width') || 0);
    return saved >= 320 && saved <= 760 ? saved : 460;
  });
  const [resizing, setResizing] = useState(false);
  const [panelOpen, setPanelOpen] = useState(
    () => window.localStorage.getItem('intel-panel-open') !== 'false',
  );
  const panelRef = useRef<HTMLDivElement | null>(null);
  const widthRef = useRef(panelWidth);
  widthRef.current = panelWidth;

  const layers: IntelMapLayer[] = useMemo(
    () => Object.entries(layerMap).map(([id, points]) => ({ id, points, line: lineMap[id] || null })),
    [layerMap, lineMap],
  );

  const sliderPoints = useMemo<PathfinderDataPoint[]>(() => {
    const out: PathfinderDataPoint[] = [];
    layers.forEach((layer, li) => {
      (layer.points || []).forEach((p, i) => {
        if (!p.timestamp) return;
        const ts = new Date(p.timestamp);
        if (Number.isNaN(ts.getTime())) return;
        out.push({
          id: `${layer.id}-${i}`,
          deviceId: p.entityId || p.label || `${li}-${i}`,
          studentId: p.entityId || p.label || 'packet',
          timestamp: ts,
          coordinates: [p.latitude, p.longitude],
          accuracy: 0,
        });
      });
    });
    return out;
  }, [layers]);

  const timeRange = useMemo(() => {
    if (sliderPoints.length === 0) {
      const end = new Date();
      return { start: new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000), end };
    }
    let min = sliderPoints[0].timestamp.getTime();
    let max = min;
    for (const p of sliderPoints) {
      const t = p.timestamp.getTime();
      if (t < min) min = t;
      if (t > max) max = t;
    }
    if (max <= min) max = min + 60 * 60 * 1000;
    return { start: new Date(min), end: new Date(max) };
  }, [sliderPoints]);

  const visibleLayers = useMemo(() => {
    if (fullExtent || !timeWindow) return layers;
    const start = timeWindow.start.getTime();
    const end = timeWindow.end.getTime();
    return layers.map((layer) => ({
      ...layer,
      points: (layer.points || []).filter((p) => {
        if (!p.timestamp) return true;
        const t = new Date(p.timestamp).getTime();
        return Number.isFinite(t) && t >= start && t <= end;
      }),
    }));
  }, [layers, fullExtent, timeWindow]);

  useEffect(() => {
    if (!resizing) return;
    const prevSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    const onMove = (e: MouseEvent) => {
      const left = panelRef.current?.getBoundingClientRect().left ?? 0;
      const next = Math.min(760, Math.max(320, e.clientX - left));
      widthRef.current = next;
      setPanelWidth(next);
    };
    const onUp = () => {
      setResizing(false);
      window.localStorage.setItem('intel-panel-width', String(widthRef.current));
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      document.body.style.userSelect = prevSelect;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizing]);

  useEffect(() => {
    window.localStorage.setItem('intel-panel-open', String(panelOpen));
  }, [panelOpen]);

  const handleSelect = useCallback((next: FileSelection | null) => {
    if (!next) navigate('/intel');
    else navigate(next.kind === 'mission' ? `/intel/mission/${next.id}` : `/intel/${next.id}`);
  }, [navigate]);

  const focusPoint = useCallback((longitude: number, latitude: number) => {
    setCenter([latitude, longitude]);
    setZoom(16);
    mapRef.current?.flyTo?.({
      center: [longitude, latitude],
      zoom: 16,
      duration: 700,
    });
  }, []);

  const handleLoadLayer = useCallback((
    layerId: string,
    points: PacketMapPoint[],
    line?: GeoJSON.LineString | null,
  ) => {
    setLayerMap((prev) => ({ ...prev, [layerId]: points || [] }));
    setLineMap((prev) => {
      if (!line) {
        if (!prev[layerId]) return prev;
        const next = { ...prev };
        delete next[layerId];
        return next;
      }
      return { ...prev, [layerId]: line };
    });
  }, []);

  const handleClearLayers = useCallback((pid: number) => {
    const prefix = `intel-packet-${pid}`;
    const strip = <T,>(prev: Record<string, T>) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith(prefix)) delete next[key];
      });
      return next;
    };
    setLayerMap(strip);
    setLineMap(strip);
  }, []);

  /**
   * Push a mission's stops and watched areas onto the map.
   *
   * The line itself is MissionRouteLayer (travelled grey / remaining blue).
   * Separate from selecting one because a reroute taken from the Sentinel
   * thread rewrites `route_geometry` without the selection changing.
   */
  const drawMission = useCallback((shown: Mission) => {
    handleLoadLayer(MISSION_LAYER, [], null);
    setGeofences(
      (shown.geofences || [])
        .filter((f) => (f.geometry?.coordinates?.[0]?.length || 0) > 2)
        .map((f) => ({
          label: f.name,
          entityId: `geofence-${f.geofence_id}`,
          coordinates: f.geometry!.coordinates[0],
        })),
    );
  }, [handleLoadLayer]);

  const handleShowMission = useCallback((shown: Mission | null) => {
    setMission(shown);
    // A status belongs to the mission it was polled for; carrying it across a
    // selection would badge the new mission with the old one's alerts. Only on
    // a genuine change of mission, though — this also runs when the workspace
    // re-reads the mission it already had, and blanking the run then would
    // flicker the alert counts every time somebody takes a reroute.
    const shownId = shown?.id ?? null;
    if (shownIdRef.current !== shownId) {
      shownIdRef.current = shownId;
      setRunStatus(null);
      setOptionPreview(null);
    }
    if (!shown) {
      setLayerMap((prev) => {
        if (!prev[MISSION_LAYER]) return prev;
        const next = { ...prev };
        delete next[MISSION_LAYER];
        return next;
      });
      setLineMap((prev) => {
        if (!prev[MISSION_LAYER]) return prev;
        const next = { ...prev };
        delete next[MISSION_LAYER];
        return next;
      });
      setGeofences([]);
      return;
    }
    drawMission(shown);
  }, [drawMission]);

  const refreshMission = useCallback(async () => {
    const id = mission?.id;
    if (!id) return;
    try {
      const fresh = await getMission(id);
      setMission(fresh);
      // Redrawn, not just restated: the caller refreshes because something
      // changed, and the route is the thing most often changing.
      drawMission(fresh);
      // The panel on the other side of the map holds its own copy and would
      // otherwise go on quoting the old route's distance.
      setMissionSignal((n) => n + 1);
    } catch {
      // Leaving the dock on the last good mission beats blanking it over a
      // transient error.
    }
  }, [mission?.id, drawMission]);

  const showMissionDock = selected?.kind === 'mission' && !!mission;

  // The dock can pull directions, places, events and corridor findings against
  // the mission's saved line, so forgetting to scan while planning is not a
  // reason to reopen the planner.
  const scan = useMissionScan(mission, { onPersisted: refreshMission });

  // Only the mission branch of the panel reports a mission, so moving to a
  // packet would otherwise leave the previous one's pins and dock behind.
  useEffect(() => {
    if (selected?.kind !== 'mission') {
      setMission(null);
      setRunStatus(null);
    }
  }, [selected]);

  useStopSentinelLive(mission);
  const deviceTracks = useMissionDeviceTracks(mission, runStatus?.devices);
  const trailLead = useMemo(
    () => pickTrailLead(deviceTracks, runStatus?.progress?.lead_device_id),
    [deviceTracks, runStatus?.progress?.lead_device_id],
  );
  const convoyRedirect = useConvoyRedirect({
    mission,
    status: runStatus,
    tracks: deviceTracks,
    onApplied: refreshMission,
  });

  const areaDraw = useAreaDraw(mission, { onSaved: refreshMission, onError: () => undefined });

  const dockLive = useMissionDockLive({
    mission,
    onMissionChange: refreshMission,
    onStatus: setRunStatus,
    onFocus: focusPoint,
    onPreviewOption: setOptionPreview,
  });

  const openAlerts = useMemo(() => {
    if (!runStatus?.events) return null;
    return criticalAlertCount(runStatus.events, (id) => id);
  }, [runStatus]);

  const [pressingFollows, setPressingFollows] = useState<number | null>(null);
  const [areaCount, setAreaCount] = useState<number | null>(null);
  const [photoCount, setPhotoCount] = useState<number | null>(null);
  const [dwellCount, setDwellCount] = useState<number | null>(null);
  const [signalCount, setSignalCount] = useState<number | null>(null);
  const [reportCount, setReportCount] = useState<number | null>(null);

  useEffect(() => {
    setAreaCount(null);
    setPhotoCount(null);
    setDwellCount(null);
    setSignalCount(null);
    setReportCount(null);
  }, [mission?.id]);
  const waypointPickRef = useRef<((longitude: number, latitude: number) => void) | null>(null);
  const mapRef = useRef<any>(null);
  const [pickingWaypoint, setPickingWaypoint] = useState(false);

  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      {!panelOpen && (
        <Box
          sx={{
            position: 'relative',
            width: 18,
            flexShrink: 0,
            height: '100%',
            display: { xs: 'none', md: 'block' },
            bgcolor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider',
          }}
        >
          <PanelEdgeTab side="right" title="Open panel" onClick={() => setPanelOpen(true)}>
            <ChevronRightIcon sx={{ fontSize: 16 }} />
          </PanelEdgeTab>
        </Box>
      )}
      <Box
        ref={panelRef}
        sx={{
          position: 'relative',
          width: { xs: '100%', md: panelWidth },
          maxWidth: { xs: '100%', md: panelWidth },
          flexShrink: 0,
          height: '100%',
          minHeight: 0,
          borderRight: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: { xs: selected ? 'flex' : 'block', md: panelOpen ? 'flex' : 'none' },
          flexDirection: 'column',
          overflow: 'visible',
        }}
      >
        <PanelEdgeTab side="right" title="Collapse" onClick={() => setPanelOpen(false)}>
          <ChevronLeftIcon sx={{ fontSize: 16 }} />
        </PanelEdgeTab>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <IntelPacketPanel
          intelEnabled={intelEnabled}
          selected={selected}
          onSelect={handleSelect}
          onZoomTo={(lat, lng, nextZoom) => {
            setCenter([lat, lng]);
            setZoom(nextZoom ?? 15);
          }}
          onLoadPacketLayer={handleLoadLayer}
          onLoadPacketGeofences={(_pid, fences) => setGeofences(fences || [])}
          onClearPacketLayers={(pid) => {
            handleClearLayers(pid);
            setGeofences([]);
          }}
          onShowMission={handleShowMission}
          missionSignal={missionSignal}
          onFocusPoint={focusPoint}
          liveStatus={runStatus}
          onLiveStatus={setRunStatus}
          onWaypointPickChange={setPickingWaypoint}
          waypointPickRef={waypointPickRef}
          onEmergencyReroute={convoyRedirect.start}
          onCancelEmergencyReroute={convoyRedirect.cancel}
          emergencyReroutePicking={convoyRedirect.picking}
          emergencyRerouteBusy={convoyRedirect.busy}
          emergencyRerouteError={convoyRedirect.error}
          onOpenPhotos={() => setDockTabRequest({ id: 'photos', key: Date.now() })}
        />
        </Box>
      </Box>
      <Box
        onMouseDown={(e) => { e.preventDefault(); setResizing(true); }}
        sx={{
          display: { xs: 'none', md: panelOpen ? 'block' : 'none' },
          width: 6,
          cursor: 'col-resize',
          flexShrink: 0,
          bgcolor: resizing ? 'rgba(245,185,66,0.55)' : 'transparent',
          '&:hover': { bgcolor: 'rgba(245,185,66,0.35)' },
        }}
      />
      {/* Basis rather than `flex: 1`, so the panel and the reference dock take
          width from the map in proportion instead of leaving it the remainder. */}
      <Box sx={{
        flex: '1 1 480px',
        minWidth: 280,
        display: { xs: 'none', md: 'block' },
        height: '100%',
        position: 'relative',
      }}>
        <IntelPacketMap
          center={center}
          zoom={zoom}
          layers={visibleLayers}
          geofences={geofences}
          liveLayers={!!mission}
          onOpenMapSettings={() => setMapSettingsOpen(true)}
          captureClicks={pickingWaypoint || areaDraw.drawing || convoyRedirect.picking}
          cursor={pickingWaypoint || areaDraw.drawing || convoyRedirect.picking ? 'crosshair' : undefined}
          onMapClick={(ll) => {
            if (areaDraw.drawing) {
              areaDraw.addVertex(ll.lng, ll.lat);
              return;
            }
            if (convoyRedirect.picking) {
              void convoyRedirect.applyClick(ll.lng, ll.lat);
              return;
            }
            waypointPickRef.current?.(ll.lng, ll.lat);
          }}
          onMapReady={(map) => { mapRef.current = map; }}
          onMove={(nextCenter, nextZoom) => {
            setCenter(nextCenter);
            setZoom(nextZoom);
          }}
        >
          <MissionDeviceLayer
            tracks={deviceTracks}
            interactive={!pickingWaypoint && !areaDraw.drawing && !convoyRedirect.picking}
            onSelect={(track) => focusPoint(track.longitude, track.latitude)}
          />
          {areaDraw.drawing && <AreaDrawPreview vertices={areaDraw.vertices} />}
          {mission && (
            <MissionRouteLayer
              geometry={mission.route_geometry}
              alternate={mission.alt_route?.geometry}
              prior={mission.prior_route?.geometry}
              progress={reportedProgress(runStatus?.progress)}
              lead={trailLead}
            />
          )}
          {mission && (
            <MissionWaypointLayer
              waypoints={mission.waypoints || []}
              interactive={!pickingWaypoint && !areaDraw.drawing && !convoyRedirect.picking}
              onSelect={(lat, lon) => focusPoint(lon, lat)}
            />
          )}
          {mission && (
            <MissionThreatLayer
              missionId={mission.id}
              events={runStatus?.events}
              refreshKey={runStatus?.events?.length}
              interactive={!pickingWaypoint && !convoyRedirect.picking}
              onSelect={(lat, lon) => focusPoint(lon, lat)}
              clearedEventIds={optionPreview?.focus?.clears?.map((c) => c.event_id)}
              exposedEventIds={optionPreview?.focus?.still_exposed?.map((c) => c.event_id)}
            />
          )}
          <MissionOptionPreviewLayer preview={optionPreview} />
          {mission && (
            <MissionMediaLayer
              missionId={mission.id}
              refreshKey={runStatus?.events?.length}
              interactive={!pickingWaypoint && !convoyRedirect.picking}
              onSelect={(lat, lon) => focusPoint(lon, lat)}
            />
          )}
          {mission && (
            <MissionDwellLayer
              missionId={mission.id}
              started={!!mission.started_at || mission.status === 'in_progress'}
              refreshKey={runStatus?.events?.length}
              interactive={!pickingWaypoint && !convoyRedirect.picking}
              onSelect={(lat, lon) => focusPoint(lon, lat)}
            />
          )}
        </IntelPacketMap>
        {mission && (
          <MissionVehicleBar
            missionId={mission.id}
            enabled={mission.status === 'in_progress' || mission.status === 'baseline'}
            onSent={setRunStatus}
          />
        )}
        {mission && (
          <MissionVideoStrip tracks={deviceTracks} onLocate={focusPoint} />
        )}
        {mission && <MissionChatToast />}
        <AreaDrawOverlay draw={areaDraw} />
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            bottom: 12,
            transform: 'translateX(-50%)',
            width: { xs: 'calc(100% - 24px)', md: 'min(920px, calc(100% - 32px))' },
            zIndex: 1200,
            bgcolor: theme.palette.mode === 'dark'
              ? 'rgba(18, 20, 24, 0.88)'
              : 'rgba(255, 255, 255, 0.90)',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
            backdropFilter: 'blur(8px)',
            overflow: 'hidden',
          }}
        >
          <CompactTimeSlider
            dataPoints={sliderPoints}
            routes={[]}
            timeRange={timeRange}
            selectedStudents={[]}
            selectedDevices={{}}
            playbackOnly
            mapSettingsOpen={mapSettingsOpen}
            onOpenMapSettings={() => setMapSettingsOpen(true)}
            onMapSettingsClose={() => setMapSettingsOpen(false)}
            onTimeUpdate={(_time, _points, _routes, isFull, window) => {
              setFullExtent(!!isFull);
              setTimeWindow(window || null);
            }}
          />
        </Box>
      </Box>

      {showMissionDock && (
        <RouteReferenceDock
          intel={scan.intel}
          hasRoute={!!mission?.route_geometry}
          hasDepartAt={scan.hasDepartAt}
          geometry={mission?.route_geometry || null}
          openAlerts={openAlerts}
          preferLive={mission?.status === 'in_progress'}
          requestTab={dockTabRequest?.id}
          requestTabKey={dockTabRequest?.key}
          banner={mission ? dockLive.banner : undefined}
          sentinel={mission ? dockLive.sentinel : undefined}
          activity={mission ? dockLive.activity : undefined}
          chat={mission ? <MissionCrewChat /> : undefined}
          areaCount={areaCount}
          areas={mission ? (
            <MissionAreasTab
              key={mission.id}
              mission={mission}
              deviceLabel={(id) => id}
              onFocus={focusPoint}
              onCount={setAreaCount}
              watchlist={watchlistLabels(runStatus?.events)}
              onDrawArea={areaDraw.start}
              onMissionChange={refreshMission}
            />
          ) : undefined}
          photoCount={photoCount}
          photos={mission ? (
          <MissionPhotosTab
            key={mission.id}
            missionId={mission.id}
            missionName={mission.name}
            refreshKey={runStatus?.events?.length}
            timeline={runStatus?.timeline}
            canAlert={mission.status === 'in_progress' || mission.status === 'baseline'}
            onFocus={focusPoint}
            onCount={setPhotoCount}
            onAlerted={setRunStatus}
          />
        ) : undefined}
        dwellCount={dwellCount}
        dwells={mission ? (
          <MissionDwellsTab
            key={`dwells-${mission.id}`}
            missionId={mission.id}
            started={!!mission.started_at || mission.status === 'in_progress'}
            refreshKey={runStatus?.events?.length}
            onFocus={focusPoint}
            onCount={setDwellCount}
          />
        ) : undefined}
        reportCount={reportCount}
        report={mission ? (
            <MissionReportTab
              key={`report-${mission.id}`}
              mission={mission}
              onError={() => undefined}
              onMissionChange={refreshMission}
              refreshKey={runStatus?.events?.length}
              onCount={setReportCount}
            />
          ) : undefined}
        pressingFollows={pressingFollows}
        follow={mission ? (
            <MissionCotravelers
              key={mission.id}
              missionId={mission.id}
              refreshKey={runStatus?.events?.length}
              onFocus={(lat, lon) => focusPoint(lon, lat)}
              onCountChange={setPressingFollows}
              canAlert={mission.status === 'in_progress' || mission.status === 'baseline'}
              onAlerted={setRunStatus}
              events={runStatus?.events}
              timeline={runStatus?.timeline}
            />
          ) : undefined}
        signalCount={signalCount}
        signals={mission ? (
          <MissionSignalsTab
            key={`signals-${mission.id}`}
            missionId={mission.id}
            live={mission.status === 'in_progress' || mission.status === 'baseline'}
            hasAreas={mission.geofences.length > 0}
            onFocus={focusPoint}
            onCount={setSignalCount}
          />
        ) : undefined}
          onFocusAsset={(asset) => focusPoint(asset.longitude, asset.latitude)}
          onFocusEvent={(event) => focusPoint(event.longitude, event.latitude)}
          onFocusAlert={(lat, lon) => focusPoint(lon, lat)}
          onFocusSegment={(segment) => focusPoint(segment.midpoint[0], segment.midpoint[1])}
          placesRadiusM={scan.placesRadiusM}
          onPlacesRadiusChange={scan.setPlacesRadiusM}
          eventsCorridorM={scan.eventsCorridorM}
          onEventsCorridorChange={scan.setEventsCorridorM}
          eventsHorizonHours={scan.eventsHorizonHours}
          onEventsHorizonChange={scan.setEventsHorizonHours}
          eventsWindowStart={scan.eventsWindowStart}
          eventsWindowEnd={scan.eventsWindowEnd}
          onEventsWindowStartChange={scan.setEventsWindowStart}
          onEventsWindowEndChange={scan.setEventsWindowEnd}
          corridorSettings={scan.corridorSettings}
          onCorridorSettingsChange={scan.setCorridorSettings}
        />
      )}
    </Box>
  );
};

export default IntelPacketPage;
